import React, { useEffect, useState, useRef } from "react";
import { Tab, Box } from "@mui/material";
import { TabList, TabPanel, TabContext } from "@mui/lab";
import { BsSearch } from "react-icons/bs";
import { MdKeyboardArrowDown } from "react-icons/md";
import { BsThreeDots } from "react-icons/bs";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate, useParams, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import BaseUrl from "../../Components/BaseUrl";
import { set } from "date-fns";
import { se, sl } from "date-fns/locale";

import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Search,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Users,
} from "lucide-react";



const Avatar = ({ src, alt, size = "medium" }) => {
  const sizeClasses = {
    small: "w-8 h-8",
    medium: "w-12 h-12",
    large: "w-16 h-16",
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-full overflow-hidden flex-shrink-0`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.src = `https://ui-avatars.com/api/?name=${alt.charAt(
            0
          )}&background=5566c2&color=fff&size=128`;
        }}
      />
    </div>
  );
};

const Button = ({
  children,
  variant = "primary",
  size = "medium",
  disabled = false,
  onClick,
  className = "",
}) => {
  const baseClasses =
    "font-medium rounded-full focus:outline-none transition-all duration-200";
  const variantClasses = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300",
    secondary:
      "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400",
  };
  const sizeClasses = {
    small: "px-3 py-1 text-sm",
    medium: "px-4 py-2",
    large: "px-6 py-3 text-lg",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const SearchBar = ({ value, onChange, placeholder = "Search..." }) => (
  <div className="relative w-full">
    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
      <Search className="h-5 w-5 text-gray-400" />
    </div>
    <input
      type="text"
      className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm placeholder-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 shadow-sm hover:shadow-md"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const FilterButton = ({ label, isActive = false, onClick }) => (
  <button
    className={`flex items-center px-4 py-2 rounded-full border ${
      isActive
        ? "border-blue-600 bg-blue-50 text-blue-600"
        : "border-gray-300 hover:bg-gray-50"
    } transition-colors duration-200`}
    onClick={onClick}
  >
    <span className="mr-1">{label}</span>
    <ChevronDown className="h-4 w-4" />
  </button>
);

const FilterSection = () => {
  const [activeFilters, setActiveFilters] = useState([]);
  const toggleFilter = (filter) => {
    setActiveFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter]
    );
  };

  return (
    <div className="flex flex-wrap gap-2 mt-4 pb-4 border-b border-gray-200">
      <FilterButton
        label="Locations"
        isActive={activeFilters.includes("Locations")}
        onClick={() => toggleFilter("Locations")}
      />
      <FilterButton
        label="Current company"
        isActive={activeFilters.includes("Current company")}
        onClick={() => toggleFilter("Current company")}
      />
      <FilterButton
        label="School"
        isActive={activeFilters.includes("School")}
        onClick={() => toggleFilter("School")}
      />
      <button className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors duration-200">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const CheckBox = ({ checked, onChange, disabled = false }) => (
  <div
    className={`w-6 h-6 rounded-sm flex items-center justify-center transition-colors duration-200 ${
      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
    } ${
      checked
        ? "bg-green-600 border border-green-600"
        : "border border-gray-300"
    }`}
    onClick={disabled ? undefined : onChange}
  >
    {checked && <Check className="h-4 w-4 text-white" />}
  </div>
);

const ConnectionItem = ({ connection, onToggle }) => {
  const { user_id, name, headline, profile,isSelected, already_present, role } =
    connection;

  const isOwner = role === "Owner";
  const isManager = role === "Manager";
  const shouldHideCheckbox = already_present || isOwner || isManager;

  return (
    <div className="flex items-start gap-3 py-4 px-4 border-b border-gray-200 group hover:bg-gray-50 transition-colors duration-200">
      <div className="w-6 flex justify-center items-start">
        {!shouldHideCheckbox && (
          <CheckBox
            checked={!!connection.isSelected}
            onChange={() => onToggle(user_id)}
          />
        )}
      </div>
      <Avatar src={profile ? profile : "/Images/user-img.jpg"} alt={name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center">
          <h3 className="font-medium text-gray-900">{name}</h3>
          {(isOwner || isManager) && (
            <span className="ml-auto text-gray-500 text-sm">{role}</span>
          )}
        </div>
        <p className="text-sm text-gray-600 truncate">{headline}</p>
      </div>
    </div>
  );
};



const ConnectionsList = ({ connections, onToggle }) => (
  <div className="max-h-[400px] overflow-y-auto ">
    {connections.map((connection) => (
      <ConnectionItem
        key={connection.user_id}
        connection={connection}
        onToggle={onToggle}
      />
    ))}
  </div>
);

const SelectedConnectionsList = ({
  connections,
  onUnselect,
  onUnselectAll,
}) => {
  return (
    <div className="p-4 min-w-[200px]">
      <div className="flex justify-between items-center mb-4">
        <span className="text-gray-700 font-medium">
          {connections.length} selected
        </span>
        <button
          className="text-blue-600 hover:underline text-sm"
          onClick={onUnselectAll}
        >
          Unselect all
        </button>
      </div>

      <div className="space-y-3">
        {connections.map((connection) => (
          <div key={connection.user_id} className="flex items-center gap-2">
            <Avatar
              src={connection.profile   ? connection.profile : "/Images/user-img.jpg"}
              alt={connection.name}
              size="small"
            />
            <span className="text-sm flex-1 truncate">{connection.name}</span>
            <button
              className="text-gray-400 hover:text-gray-600"
              onClick={() => onUnselect(connection.user_id)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ConnectionModal = ({ isOpen, onClose,connection,slug,getInvites }) => {
  const [connections, setConnections] = useState(
    connection.map((c) => ({
      ...c,
      isSelected: false, 
    }))
  );

  const [searchQuery, setSearchQuery] = useState("");

  const toggleSelection = useCallback((user_id) => {
    setConnections((prev) =>
      prev.map((connection) =>
        connection.user_id === user_id && !connection.isOwner
          ? { ...connection, isSelected: !connection.isSelected }
          : connection
      )
    );
  }, []);

  const unselectAll = useCallback(() => {
    setConnections((prev) =>
      prev.map((connection) => ({ ...connection, isSelected: false }))
    );
  }, []);

  const filteredConnections = useMemo(() => {
    if (!searchQuery) return connections;
    return connections.filter((connection) =>
      connection.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [connections, searchQuery]);

  const selectedConnections = useMemo(
    () => connections.filter((connection) => connection.isSelected),
    [connections]
  );
  const sendInvite = async (userIds) => {
    try {
      const response = await axios.post(
        `${BaseUrl}sendgroupinvite/${slug}`,
        {
          users: userIds, 
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        }
      );
  
      if (response.data.status === 200) {
        onClose();
        getInvites(slug);
      }
    } catch (error) {
      console.log(error);
    }
  };
  
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fadeIn">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            Invite connections
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-2 border-b border-gray-300">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name"
          />
        </div>

        <div className="flex flex-1">
          <div className="basis-[70%] overflow-y-auto border-r border-gray-200">
            {/* <FilterSection /> */}
            <ConnectionsList
              connections={filteredConnections}
              onToggle={toggleSelection}
            />
          </div>

          <div className="basis-[30%]">
            <SelectedConnectionsList
              connections={selectedConnections}
              onUnselect={toggleSelection}
              onUnselectAll={unselectAll}
            />
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <Button
            disabled={selectedConnections.length === 0}
            onClick={() =>
              sendInvite(selectedConnections.map((user) => user.user_id))
            }
          >
            Invite{" "}
            {selectedConnections.length > 0 ? selectedConnections.length : ""}
          </Button>
        </div>
      </div>
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["primary", "secondary"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

SearchBar.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

FilterButton.propTypes = {
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
};

CheckBox.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

ConnectionItem.propTypes = {
  connection: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    avatar: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    isOwner: PropTypes.bool,
  }).isRequired,
  onToggle: PropTypes.func.isRequired,
};

ConnectionsList.propTypes = {
  connections: PropTypes.arrayOf(PropTypes.object).isRequired,
  onToggle: PropTypes.func.isRequired,
};

SelectedConnectionsList.propTypes = {
  connections: PropTypes.arrayOf(PropTypes.object).isRequired,
  onUnselect: PropTypes.func.isRequired,
  onUnselectAll: PropTypes.func.isRequired,
};

ConnectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  connection: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const GroupManage = () => {
  const { slug, tabValue: urlTabValue, menuItem: urlMenuItem } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef(null);
  const [openMenuFor, setOpenMenuFor] = useState(null);
  const dotsRefs = useRef({});
  const menuRefs = useRef({});
  const navigate = useNavigate();
  const [isloading, setIsLoading] = useState("");
  const [userid, setUserId] = useState(Cookies.get("userId"));
  const [groupdata, setGroupData] = useState({});
  const [members, setMembers] = useState([]);
  const [memberscount, setMembersCount] = useState("");
  const [admins, setAdmins] = useState([]);
  const [adminscount, setAdminsCount] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [requestcount, setRequestCount] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [invites, setInvites] = useState([]);
  const [invitescount, setInvitesCount] = useState(0);
  const [blockedcount, setBlockedCount] = useState(3);
  const [searchTerm, setSearchTerm] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [connections, setConnections] = useState([]);

  const tabs = [
    { label: "Membership", value: "1" },
    { label: "Content", value: "2" },
    { label: "Analytics", value: "3" },
  ];

  const menuItems = ["Members", "Admins", "Requests", "Invited", "Blocked"];
  const contentItems = ["PendingPosts"];

  const defaultMenuItems = {
    1: "Members",
    2: "PendingPosts",
    3: "Analytics",
  };
  const tabValue = urlTabValue || "1";
  const menuItem = urlMenuItem || defaultMenuItems[tabValue] || "Members";
  const getGroupdetails = async (slug) => {
    setIsLoading(true);
    try {
      const response = await axios.get(BaseUrl + `getgroupdetails/${slug}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
          Accept: "application/json",
        },
      });

      setGroupData(response.data.data);
      setCurrentUserRole(response.data.data.is_owner ? "Owner" : "Manager");
    } catch (error) {
      console.error("Error fetching group details:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const getRequests = async (slug) => {
    try {
      const response = await axios.get(
        BaseUrl + `joinrequests/${slug}`,

        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setRequestCount(response.data.data.total);
        setProfiles(response.data.data.members);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };
  const getMembers = async (slug) => {
    try {
      const response = await axios.get(
        BaseUrl + `groupmembers/${slug}`,

        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setMembersCount(response.data.data.total);
        setMembers(response.data.data.members);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };
  const getAdmins = async (slug) => {
    try {
      const response = await axios.get(
        BaseUrl + `groupadmins/${slug}`,

        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setAdminsCount(response.data.data.total);
        setAdmins(response.data.data.Admins);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };
  const getInvites = async (slug) => {
    try {
      const response = await axios.get(
        BaseUrl + `getinviteduser/${slug}`, 

        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setInvitesCount(response.data.data.total);

        setInvites(response.data.data.members);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  }
  const getBlocked = async (slug) => {
    try {
      const response = await axios.get(
        BaseUrl + `groupblocks/${slug}`,

        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setBlockedCount(response.data.data.total);
        setBlocked(response.data.data.members);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };
  const getConnections = async () => {
    try {
      const response = await axios.get(BaseUrl + `connections/${slug}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
          Accept: "application/json",
        },
      });
      setConnections(response.data.data.members); 
      // console.log(response.data.data.members,"connections");
    } catch (error) {
      console.error("Error fetching connections:", error);
    }
  };
  useEffect(() => {
    if (slug) {
      getGroupdetails(slug);
      getMembers(slug);
      getAdmins(slug);
      getRequests(slug);
      getInvites(slug);
      getBlocked(slug);
      getConnections(slug);
    }
  }, [slug]);

  const toggleMenu = (memberId) => {
    setOpenMenuFor((prev) => (prev === memberId ? null : memberId));
  };

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (modalRef.current && !modalRef.current.contains(event.target) && !dotsRef.current.contains(event.target)) {
  //       setIsModalOpen(false);
  //     }
  //   };
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, []);

  // const profiles = [
  //   {
  //     id: 1,
  //     name: "Shivam Mantri",
  //     jobTitle: "Software Developer",
  //     company: "LogicSpice Consultancy Pvt. Ltd.",
  //     image: "path/to/image1.jpg",
  //   },
  //   {
  //     id: 2,
  //     name: "John Doe",
  //     jobTitle: "Frontend Developer",
  //     company: "Tech Corp",
  //     image: "path/to/image2.jpg",
  //   },
  // ];
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuFor == null) return;

      const dotEl = dotsRefs.current[openMenuFor];
      const menuEl = menuRefs.current[openMenuFor];
      if (
        dotEl &&
        menuEl &&
        !dotEl.contains(e.target) &&
        !menuEl.contains(e.target)
      ) {
        setOpenMenuFor(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuFor]);


  const [requestsChecked, setRequestsChecked] = useState(
    Array(profiles.length).fill(false)
  );

  useEffect(() => {
    if (!urlTabValue || !urlMenuItem) {
      navigate(`/groups/managegroup/${slug}/${tabValue}/${menuItem}`, {
        replace: true,
      });
    }
  }, [slug, tabValue, menuItem, navigate, urlTabValue, urlMenuItem]);

  const handleTabChange = (event, newValue) => {
    navigate(
      `/groups/managegroup/${slug}/${newValue}/${
        defaultMenuItems[newValue] || "Members"
      }`
    );
  };

  const handleMenuChange = (item) => {
    navigate(`/groups/managegroup/${slug}/${tabValue}/${item}`);
  };

  const handleContentChange = (item) => {
    navigate(`/groups/managegroup/${slug}/${tabValue}/${item}`);
  };

  const handleRequestsParentChange = (event) => {
    setRequestsChecked(Array(profiles.length).fill(event.target.checked));
  };

  const handleRequestChange = (index) => (event) => {
    const newChecked = [...requestsChecked];
    newChecked[index] = event.target.checked;
    setRequestsChecked(newChecked);
  };

  // Handlers for Invited section
  // const handleInvitedParentChange = (event) => {
  //   setInvitedChecked(Array(Invites.length).fill(event.target.checked));
  // };

  // const handleInvitedChange = (index) => (event) => {
  //   const newChecked = [...invitedChecked];
  //   newChecked[index] = event.target.checked;
  //   setInvitedChecked(newChecked);
  // };
  const handleInviteConnections = () => {
    setIsModalOpen(true);
  };
  const handleAcceptRejectRequest = async (status, user) => {
    try {
      const response = await axios.post(
        BaseUrl + `groupacceptrejectrequest/${slug}`,
        {
          status: status,
          user: user,
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        }
      );
      if (response.data.status === 200) {
        setProfiles((prevProfiles) =>
          prevProfiles.filter((profile) => profile.user_id !== user)
        );
        setRequestCount((prevCount) => prevCount - 1);
        getMembers(slug);
        getAdmins(slug);
      }
      console.log(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const AssignRole = async (role, user) => {
    try {
      const response = await axios.post(
        BaseUrl + `assignrole/${slug}`,
        {
          user: user,
          role: parseInt(role),
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        }
      );
      setOpenMenuFor(null);
      getMembers(slug);
      getAdmins(slug);
    } catch (error) {
      console.log(error);
    }
  };

  const RemoveRole = async (role, user) => {
    try {
      const response = await axios.post(
        BaseUrl + `removerole/${slug}`,
        {
          user: user,
          role: role,
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
            Accept: "application/json",
          },
        }
      );
      setOpenMenuFor(null);
      getMembers(slug);
      getAdmins(slug);
    } catch (error) {
      console.log(error);
    }
  };

  const RemovefromGroup = async (id) => {
    try {
      const response = await axios.post(
        BaseUrl + `withdrawrequest/${slug}/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setOpenMenuFor(null);
        getMembers(slug);
        getAdmins(slug);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };

  const handleWithdraw = async (id) => {
    try {
      const response = await axios.post(
        BaseUrl + `removerequest/${slug}/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        getInvites(slug);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };

  const BlockFromGroup = async (id) => {
    try {
      const response = await axios.post(
        BaseUrl + `blockuser/${slug}/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setOpenMenuFor(null);
        getMembers(slug);
        getAdmins(slug);
        getBlocked(slug);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };
  const UnblockFromGroup = async (id) => {
    try {
      const response = await axios.post(
        BaseUrl + `unblockuser/${slug}/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        setOpenMenuFor(null);
        getMembers(slug);
        getAdmins(slug);
        getBlocked(slug);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  };
  const DeletePostsAndComments = async (id) => {
    try {
      const response = await axios.post(
        BaseUrl + `deleteblockuserpost/${slug}/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );
      if (response.data.status === 200) {
        
        // setOpenMenuFor(null);
        // getMembers(slug);
        // getAdmins(slug);
        getBlocked(slug);
      }
    } catch (error) {
      console.error("Join request failed:", error);
    }
  }
  const filteredMembers = members.filter((member) =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredAdmins = admins.filter((admin) =>
    admin.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredProfiles = profiles.filter((profile) =>
    profile.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredInvites = invites.filter((invite) =>
    invite.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredBlocked = blocked.filter((block) =>
    block.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const renderMembershipContent = () => {
    const requestsAllChecked = requestsChecked.every(Boolean);
    const requestsIsIndeterminate =
      requestsChecked.some(Boolean) && !requestsAllChecked;

    // const invitedAllChecked = invitedChecked.every(Boolean);
    // const invitedIsIndeterminate = invitedChecked.some(Boolean) && !invitedAllChecked;

    switch (menuItem) {
      case "Members":
        return (
          <div className="">
            {memberscount === 0 ? (
              <>
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex flex-col justify-cneter items-center py-6 ">
                    <img
                      src="/Images/group/block-img.jpg"
                      className="size-40"
                    />
                    <p className="text-xl">No members</p>
                    <p className="text-md text-gray-500 w-72 text-center">
                      Once you block a member from a group, they will be removed
                      from the group and will no longer be able to request to
                      join
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* <div className=" flex items-center justify-between border border-gray-200 rounded-lg px-6 py-4 mb-2">
                  <div className="flex flex-col ">
                    <p className="font-base">Automatic welcome note</p>{" "}
                    <p className="text-gray-500 font-none text-sm">
                      New members will see the note when they visit the group.
                    </p>
                  </div>
                  <button className="flex items-center justify-center bg-white text-gray-700 border border-gray-600 h-8 px-4 py-2 rounded-full hover:bg-gray-100 hover:text-gray-900 hover:border-gray-900 hover:border-2">
                    Set up
                  </button>
                </div> */}
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                    <p className="text-xl font-semibold ">
                      Members{" "}
                      <span className="text-gray-500 text-sm">
                        &#8226; {memberscount}{" "}
                        {memberscount === 1 ? "person" : "people"}
                      </span>
                    </p>

                    <div className="relative w-full max-w-sm">
                      <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search group members"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 border border-gray-300 p-2 rounded w-full h-8 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center  space-x-2  px-6  border-b border-gray-200 py-4">
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      Locations <MdKeyboardArrowDown />{" "}
                    </button>
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      Current company <MdKeyboardArrowDown />{" "}
                    </button>
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      Industry <MdKeyboardArrowDown />{" "}
                    </button>
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      School <MdKeyboardArrowDown />{" "}
                    </button>
                  </div>

                  <div className="bg-white  rounded-lg shadow ">
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((member, index) => (
                        <div
                          key={index}
                          className=" flex items-center justify-between space-x-4 border-b py-3 px-6"
                        >
                          <div className="flex items-center space-x-4">
                            {member.profile ? (
                              <img
                                src={member.profile}
                                alt={member.name || "User"}
                                className="w-12 h-12 rounded-full object-cover border border-gray-200"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg border border-gray-200">
                                {(member.name?.[0] || "U").toUpperCase()}
                              </div>
                            )}

                            <div>
                              <p className="font-semibold">
                                {member.name}{" "}
                                {member.role && (
                                  <span className="text-sm bg-gray-300 px-2 py-1 rounded">
                                    {member.role}
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-600">
                                {member.headline}
                              </p>
                              {member.assigned_by && (
                                <p className="text-xs text-gray-600 leading-0">
                                  Made{" "}
                                  {member.role === "Owner"
                                    ? "admin"
                                    : "manager"}{" "}
                                  by {member.assigned_by}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className=" flex items-center space-x-4">
                            <div className="relative">
                              {currentUserRole === "Manager" &&
                              member.role === "Owner" ? (
                                " "
                              ) : (
                                <BsThreeDots
                                  ref={(el) =>
                                    (dotsRefs.current[member.user_id] = el)
                                  }
                                  className="text-3xl font-bold text-gray-700 p-1 hover:text-gray-900 hover:cursor-pointer hover:bg-gray-200 hover:rounded-full "
                                  onClick={() => toggleMenu(member.user_id)}
                                />
                              )}

                              {openMenuFor === member.user_id && (
                                <div
                                  ref={(el) =>
                                    (menuRefs.current[member.user_id] = el)
                                  }
                                  className="absolute w-48 bg-white border border-gray-200 text-sm rounded-l-lg rounded-b-lg shadow-lg z-50 right-0 top-8"
                                >
                                  {currentUserRole === "Owner" ? (
                                    <>
                                      {userid === String(member.user_id) &&
                                      member.role === "Owner" ? (
                                        <button
                                          className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                          onClick={() =>
                                            RemoveRole("1", member.user_id)
                                          }
                                        >
                                          Remove admin permissions
                                        </button>
                                      ) : member.role === "Owner" ? (
                                        <>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemoveRole("1", member.user_id)
                                            }
                                          >
                                            Remove admin permissions
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemovefromGroup(member.user_id)
                                            }
                                          >
                                            Remove from group
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              BlockFromGroup(member.user_id)
                                            }
                                          >
                                            Block from group
                                          </button>
                                        </>
                                      ) : member.role === "Manager" ? (
                                        <>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemoveRole("2", member.user_id)
                                            }
                                          >
                                            Remove admin permissions
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              AssignRole("1", member.user_id)
                                            }
                                          >
                                            Make owner
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemovefromGroup(member.user_id)
                                            }
                                          >
                                            Remove from group
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              BlockFromGroup(member.user_id)
                                            }
                                          >
                                            Block from group
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              AssignRole("2", member.user_id)
                                            }
                                          >
                                            Make manager
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              AssignRole("1", member.user_id)
                                            }
                                          >
                                            Make owner
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemovefromGroup(member.user_id)
                                            }
                                          >
                                            Remove from group
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              BlockFromGroup(member.user_id)
                                            }
                                          >
                                            Block from group
                                          </button>
                                        </>
                                      )}
                                    </>
                                  ) : currentUserRole === "Manager" ? (
                                    <>
                                      {member.role ===
                                      "Owner" ? null : userid ===
                                        String(member.user_id) ? (
                                        // Manager acting on themselves
                                        <button
                                          className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                          onClick={() =>
                                            RemoveRole("2", member.user_id)
                                          }
                                        >
                                          Remove admin permissions
                                        </button>
                                      ) : member.role === "Manager" ? (
                                        <>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemoveRole("2", member.user_id)
                                            }
                                          >
                                            Remove admin permissions
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemovefromGroup(member.user_id)
                                            }
                                          >
                                            Remove from group
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              BlockFromGroup(member.user_id)
                                            }
                                          >
                                            Block from group
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              AssignRole("2", member.user_id)
                                            }
                                          >
                                            Make manager
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              RemovefromGroup(member.user_id)
                                            }
                                          >
                                            Remove from group
                                          </button>
                                          <button
                                            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                            onClick={() =>
                                              BlockFromGroup(member.user_id)
                                            }
                                          >
                                            Block from group
                                          </button>
                                        </>
                                      )}
                                    </>
                                  ) : null}
                                </div>
                              )}
                            </div>

                            {userid === String(member.user_id) ? (
                              ""
                            ) : (
                              <button className="flex items-center justify-center bg-white text-blue-700 border border-blue-600 h-8 px-4 py-2 rounded-full hover:bg-blue-100 hover:text-blue-900 hover:border-blue-800 hover:border-2">
                                Message
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col justify-center items-center py-10 text-gray-500">
                        <img
                          src="/Images/group/block-img.jpg"
                          alt="No Results"
                          className="w-32 h-32 mb-4"
                        />
                        <p className="text-lg font-semibold">
                          No matching members found
                        </p>
                        <p className="text-sm text-gray-400 mt-2 text-center">
                          Try searching with a different name or keyword.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Admins":
        return (
          <div className="">
            {adminscount === 0 ? (
              <>
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex flex-col justify-cneter items-center py-6 ">
                    <img
                      src="/Images/group/block-img.jpg"
                      className="size-40"
                    />
                    <p className="text-xl">No Admins</p>
                    <p className="text-md text-gray-500 w-72 text-center">
                      Once you block a member from a group, they will be removed
                      from the group and will no longer be able to request to
                      join
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                  <p className="text-xl font-semibold ">
                    Admins{" "}
                    <span className="text-gray-500 text-sm">
                      &#8226; {adminscount}{" "}
                      {adminscount === 1 ? "person" : "people"}
                    </span>
                  </p>

                  <div className="relative w-full max-w-sm">
                    <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search group members...."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border border-gray-300 p-2 rounded w-full h-8 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center  space-x-2  px-6  border-b border-gray-200 py-4">
                  <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Locations <MdKeyboardArrowDown />{" "}
                  </button>
                  <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Current company <MdKeyboardArrowDown />{" "}
                  </button>
                  <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Industry <MdKeyboardArrowDown />{" "}
                  </button>
                  <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    School <MdKeyboardArrowDown />{" "}
                  </button>
                </div>

                <div className="bg-white  rounded-lg shadow ">
                  {filteredAdmins.length > 0 ? (
                    filteredAdmins.map((admin, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between space-x-4 border-b py-3 px-6"
                      >
                        <div className="flex items-center space-x-4">
                          {admin.profile ? (
                            <img
                              src={admin.profile}
                              alt={admin.name || "User"}
                              className="w-12 h-12 rounded-full object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold text-lg border border-gray-200">
                              {(admin.name?.[0] || "U").toUpperCase()}
                            </div>
                          )}

                          <div>
                            <p className="font-semibold">
                              {admin.name}{" "}
                              {admin.role ? (
                                <span className="text-sm bg-gray-300 px-2 py-1 rounded">
                                  {admin.role}
                                </span>
                              ) : (
                                ""
                              )}
                            </p>
                            <p className="text-sm text-gray-600">
                              {admin.headline}
                            </p>
                            {admin.assigned_by && (
                              <p className="text-xs text-gray-600">
                                Made{" "}
                                {admin.role === "Owner" ? "admin" : "manager"}{" "}
                                by {admin.assigned_by}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="relative">
                            <BsThreeDots
                              ref={(el) =>
                                (dotsRefs.current[admin.user_id] = el)
                              }
                              className="text-3xl font-bold text-gray-700 p-1 hover:text-gray-900 hover:cursor-pointer hover:bg-gray-200 hover:rounded-full "
                              onClick={() => toggleMenu(admin.user_id)}
                            />
                            {openMenuFor === admin.user_id && (
                              <div
                                ref={(el) =>
                                  (menuRefs.current[admin.user_id] = el)
                                }
                                className="absolute w-48 bg-white border border-gray-200 text-sm rounded-l-lg rounded-b-lg shadow-lg z-50 right-0 top-8"
                              >
                                {/* need to check for the if user is manager then how to handle the owner */}
                                {userid === String(admin.user_id) &&
                                admin.role === "Owner" ? (
                                  <button
                                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                    onClick={() =>
                                      RemoveRole("1", admin.user_id)
                                    }
                                  >
                                    Remove admin permissions
                                  </button>
                                ) : userid !== String(admin.user_id) &&
                                  admin.role === "Owner" ? (
                                  <>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemoveRole("1", admin.user_id)
                                      }
                                    >
                                      Remove admin permissions
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemovefromGroup(admin.user_id)
                                      }
                                    >
                                      Remove from group
                                    </button>
                                    <button className="block w-full text-left px-4 py-2 hover:bg-gray-100">
                                      Block from group
                                    </button>
                                  </>
                                ) : userid !== String(admin.user_id) &&
                                  admin.role === "Manager" ? (
                                  <>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemoveRole("2", admin.user_id)
                                      }
                                    >
                                      Remove admin permissions
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        AssignRole("1", admin.user_id)
                                      }
                                    >
                                      Make owner
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemovefromGroup(admin.user_id)
                                      }
                                    >
                                      Remove from group
                                    </button>
                                    <button className="block w-full text-left px-4 py-2 hover:bg-gray-100">
                                      Block from group
                                    </button>
                                  </>
                                ) : userid !== String(admin.user_id) &&
                                  admin.role === "" ? (
                                  <>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        AssignRole("2", admin.user_id)
                                      }
                                    >
                                      Make manager
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        AssignRole("1", admin.user_id)
                                      }
                                    >
                                      Make owner
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemovefromGroup(admin.user_id)
                                      }
                                    >
                                      Remove from group
                                    </button>
                                    <button className="block w-full text-left px-4 py-2 hover:bg-gray-100">
                                      Block from group
                                    </button>
                                  </>
                                ) : userid === String(admin.user_id) &&
                                  admin.role === "Manager" ? (
                                  <>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemoveRole("1", admin.user_id)
                                      }
                                    >
                                      Remove admin permissions
                                    </button>
                                  </>
                                ) : userid !== String(admin.user_id) &&
                                  admin.role === "Manager" ? (
                                  <>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemoveRole("1", admin.user_id)
                                      }
                                    >
                                      Remove admin permissions
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemovefromGroup(admin.user_id)
                                      }
                                    >
                                      Remove from group
                                    </button>
                                    <button className="block w-full text-left px-4 py-2 hover:bg-gray-100">
                                      Block from group
                                    </button>
                                  </>
                                ) : userid !== String(admin.user_id) &&
                                  admin.role === " " ? (
                                  <>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        AssignRole("2", admin.user_id)
                                      }
                                    >
                                      Make manager
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemovefromGroup(admin.user_id)
                                      }
                                    >
                                      Remove from group
                                    </button>
                                    <button className="block w-full text-left px-4 py-2 hover:bg-gray-100">
                                      Block from group
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        AssignRole("2", admin.user_id)
                                      }
                                    >
                                      Make manager
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        AssignRole("1", admin.user_id)
                                      }
                                    >
                                      Make owner
                                    </button>
                                    <button
                                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                      onClick={() =>
                                        RemovefromGroup(admin.user_id)
                                      }
                                    >
                                      Remove from group
                                    </button>
                                    <button className="block w-full text-left px-4 py-2 hover:bg-gray-100">
                                      Block from group
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          {userid === String(admin.user_id) ? (
                            ""
                          ) : (
                            <button className="flex items-center justify-center bg-white text-blue-700 border border-blue-600 h-8 px-4 py-2 rounded-full hover:bg-blue-100 hover:text-blue-900 hover:border-blue-800 hover:border-2">
                              Message
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col justify-center items-center py-10 text-gray-500">
                      <img
                        src="/Images/group/block-img.jpg"
                        alt="No Results"
                        className="w-32 h-32 mb-4"
                      />
                      <p className="text-lg font-semibold">
                        No matching admins found
                      </p>
                      <p className="text-sm text-gray-400 mt-2 text-center">
                        Try searching with a different name or keyword.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "Requests":
        return (
          <div className="">
            {requestcount === 0 ? (
              <>
                <div className=" flex items-center justify-between border border-gray-200 rounded-lg px-6 py-4 mb-2">
                  <div className="flex flex-col ">
                    <p>Automatic member approval</p>{" "}
                    <p>
                      Select members who can join this group without admin
                      approval.{" "}
                    </p>
                  </div>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-full">
                    Set up
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex flex-col justify-cneter items-center py-6 ">
                    <img
                      src="/Images/group/norequests.jpg"
                      alt="No requests"
                      className="size-64 object-contain"
                    />
                    <p className="text-2xl font-semibold">
                      No requests to review
                    </p>
                    <p className="text-md w-1/3 text-center text-gray-600">
                      Nice job! Check back later for new requests to join your
                      group.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className=" flex items-center justify-between border border-gray-200 rounded-lg px-6 py-4 mb-2">
                  <div className="flex flex-col ">
                    <p>Automatic member approval</p>{" "}
                    <p>
                      Select members who can join this group without admin
                      approval.{" "}
                    </p>
                  </div>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-full">
                    Set up
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                    <p className="text-xl font-semibold ">
                      Requests{" "}
                      <span className="text-gray-500 text-sm">
                        &#8226; {requestcount} Person
                      </span>{" "}
                    </p>

                    <div className="relative w-full max-w-sm">
                      <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search group members...."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 border border-gray-300 p-2 rounded w-full h-8 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center  space-x-2  px-6  border-b border-gray-200 py-4">
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      Locations <MdKeyboardArrowDown />{" "}
                    </button>
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      Current company <MdKeyboardArrowDown />{" "}
                    </button>
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      Industry <MdKeyboardArrowDown />{" "}
                    </button>
                    <button className=" flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                      School <MdKeyboardArrowDown />{" "}
                    </button>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 px-6 border-b border-gray-200 py-4">
                      <FormControlLabel
                        label="Select All"
                        control={
                          <Checkbox
                            checked={requestsAllChecked}
                            indeterminate={requestsIsIndeterminate}
                            onChange={handleRequestsParentChange}
                          />
                        }
                      />
                    </div>
                    {filteredProfiles.length > 0 ? (
                      filteredProfiles.map((profile, index) => (
                        <div
                          key={index}
                          className="flex justify-between px-6 border-b border-gray-200 py-2"
                        >
                          <div className="flex items-center gap-4">
                            <Checkbox
                              checked={requestsChecked[index]}
                              onChange={handleRequestChange(index)}
                            />
                            {profile.profile ? (
                              <img
                                src={profile.profile}
                                alt="Profile picture"
                                className="size-12 rounded-full border-4 border-white object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg border border-gray-200">
                                {(profile.name?.[0] || "U").toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="text-gray-800 text-lg font-semibold">
                                {profile.name}
                              </p>
                              <p className="text-gray-500">
                                {profile.headline}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <BsThreeDots className="text-3xl font-bold text-gray-700 p-1 hover:text-gray-900 hover:cursor-pointer hover:bg-gray-200 hover:rounded-full " />
                            <button
                              className="flex items-center gap-1 px-4 py-1 border border-gray-600 text-gray-600 rounded-full hover:text-gray-700 hover:bg-gray-100 transition-all duration-100 ease-in-out transform hover:scale-85"
                              onClick={() =>
                                handleAcceptRejectRequest(
                                  "rejected",
                                  profile.user_id
                                )
                              }
                            >
                              Deny
                            </button>
                            <button
                              className="flex items-center gap-1 px-4 py-1 border border-blue-600 text-blue-600 rounded-full hover:border-2 hover:text-blue-700 hover:border-blue-700 hover:bg-blue-100 transition-all duration-100 ease-in-out transform hover:scale-85"
                              onClick={() =>
                                handleAcceptRejectRequest(
                                  "accepted",
                                  profile.user_id
                                )
                              }
                            >
                              Approve
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col justify-center items-center py-10 text-gray-500">
                        <img
                          src="/Images/group/block-img.jpg"
                          alt="No Results"
                          className="w-32 h-32 mb-4"
                        />
                        <p className="text-lg font-semibold">
                          No matching Requests found
                        </p>
                        <p className="text-sm text-gray-400 mt-2 text-center">
                          Try searching with a different name or keyword.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Invited":
        return (
          <div className="">
            {invitescount === 0 ? (
              <>
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex flex-col justify-cneter items-center py-6 ">
                    <img
                      src="/Images/group/norequests.jpg"
                      alt="No requests"
                      className="size-56 object-contain"
                    />
                    <p className="text-2xl font-semibold">
                      Build your community
                    </p>
                    <p className="text-md w-1/3 text-center text-gray-700">
                      Invite your connections to join the group
                    </p>
                    <button
                      class="px-4 py-1 bg-blue-600 text-white rounded-full hover:bg-blue-800 mt-4"
                      onClick={handleInviteConnections}
                    >
                      Invite Connections
                    </button>
                  </div>
                  {isModalOpen && (
                    <ConnectionModal
                      isOpen={isModalOpen}
                      onClose={() => setIsModalOpen(false)}
                      connection={connections}
                      slug={slug}
                      getInvites={getInvites}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                  <p className="text-xl font-semibold">
                    Invited{" "}
                    <span className="text-gray-500 text-sm">
                      &#8226; {invites.length}{" "}
                      {invites.length === 1 ? "Person" : "People"}
                    </span>
                  </p>

                  <div className="relative w-full max-w-sm">
                    <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search invited members...."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border border-gray-300 p-2 rounded w-full h-8 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 px-6 border-b border-gray-200 py-4">
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Locations <MdKeyboardArrowDown />
                  </button>
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Current company <MdKeyboardArrowDown />
                  </button>
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Industry <MdKeyboardArrowDown />
                  </button>
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    School <MdKeyboardArrowDown />
                  </button>
                </div>

                <div className="bg-white rounded-lg">
                  {filteredInvites.length > 0 ? (
                    filteredInvites.map((profile) => (
                      <div
                        key={profile.user_id}
                        className="flex justify-between items-center px-6 border-b border-gray-200 py-4"
                      >
                        <div className="flex items-center gap-4">
                          {profile.image ? (
                            <img
                            src={profile.image}
                            alt="Profile picture"
                            className="w-16 h-16 rounded-full"
                          />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg border border-gray-200">
                              {(profile.name?.[0] || "U").toUpperCase()}
                            </div>
                          
                          )}
                          
                          <div>
                            <p className="text-gray-800 text-lg font-semibold">
                              {profile.name}
                            </p>
                            <p className="text-gray-500">{profile.headline}</p>
                            <p className="text-gray-500 text-sm">
                              Invited by {profile.invited_by}
                            </p>
                          </div>
                        </div>
                        <button className="flex items-center gap-1 px-4 py-1 border border-gray-600 text-gray-600 rounded-full hover:border-2 hover:text-gray-700 hover:border-gray-700 hover:bg-gray-100 transition-all duration-100 ease-in-out"
                          onClick={() => handleWithdraw(profile.user_id)}
                        >
                          Withdraw
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col justify-center items-center py-10 text-gray-500">
                      <img
                        src="/Images/group/block-img.jpg"
                        alt="No Results"
                        className="w-32 h-32 mb-4"
                      />
                      <p className="text-lg font-semibold">
                        No matching Invites found
                      </p>
                      <p className="text-sm text-gray-400 mt-2 text-center">
                        Try searching with a different name or keyword.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "Blocked":
        return (
          <div className="">
            {blockedcount === 0 ? (
              <>
                <div className="border border-gray-200 rounded-lg">
                  <div className="flex flex-col justify-cneter items-center py-6 ">
                    <img
                      src="/Images/group/block-img.jpg"
                      className="size-40"
                    />
                    <p className="text-xl">No blocked members</p>
                    <p className="text-md text-gray-500 w-72 text-center">
                      Once you block a member from a group, they will be removed
                      from the group and will no longer be able to request to
                      join
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                  <p className="text-xl font-semibold">
                    Blocked{" "}
                    <span className="text-gray-500 text-sm">
                      &#8226; {blocked.length}{" "}
                      {blocked.length === 1 ? "Person" : "People"}
                    </span>
                  </p>

                  <div className="relative w-full max-w-sm">
                    <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search blocked....."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border border-gray-300 p-2 rounded w-full h-8 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 px-6 border-b border-gray-200 py-4">
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Locations <MdKeyboardArrowDown />
                  </button>
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Current company <MdKeyboardArrowDown />
                  </button>
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    Industry <MdKeyboardArrowDown />
                  </button>
                  <button className="flex items-center space-x-2 border p-2 rounded h-8 px-4 rounded-2xl">
                    School <MdKeyboardArrowDown />
                  </button>
                </div>

                <div className="bg-white rounded-lg">
                  {filteredBlocked.length > 0 ? (
                    filteredBlocked.map((profile) => (
                      <div
                        key={profile.id}
                        className="relative flex justify-between items-center px-6 border-b border-gray-200 py-4"
                      >
                        <div className="flex items-center gap-4">
                          {profile.profile ? (
                            <img
                              src={profile.profile}
                              alt={profile.name || "User"}
                              className="w-12 h-12 rounded-full object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg border border-gray-200">
                              {(profile.name?.[0] || "U").toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="text-gray-800 text-lg font-semibold">
                              {profile.name}
                            </p>
                            <p className="text-gray-500">{profile.headline}</p>
                            <p className="text-gray-500 text-sm">
                              Blocked by {profile.assigned_by}
                            </p>
                          </div>
                        </div>
                        <BsThreeDots
                          ref={(el) => (dotsRefs.current[profile.user_id] = el)}
                          className="text-3xl font-bold text-gray-700 p-1 hover:text-gray-900 hover:cursor-pointer hover:bg-gray-200 hover:rounded-full"
                          onClick={() => toggleMenu(profile.user_id)}
                        />
                        {openMenuFor === profile.user_id && (
                          <div
                            ref={(el) =>
                              (menuRefs.current[profile.user_id] = el)
                            }
                            className="absolute w-48 bg-white border border-gray-200 text-sm rounded-l-lg rounded-b-lg shadow-lg z-50 right-8 top-16"
                          >
                            {profile.user_id && (
                              <>
                                <button
                                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                  onClick={() =>
                                    UnblockFromGroup(profile.user_id)
                                  }
                                >
                                  Unblock from group
                                </button>
                                <button
                                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                  onClick={() =>
                                    DeletePostsAndComments(profile.user_id)
                                  }
                                >
                                  Delete all posts and comments
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col justify-center items-center py-10 text-gray-500">
                      <img
                        src="/Images/group/block-img.jpg"
                        alt="No Results"
                        className="w-32 h-32 mb-4"
                      />
                      <p className="text-lg font-semibold">
                        No matching blocked members found
                      </p>
                      <p className="text-sm text-gray-400 mt-2 text-center">
                        Try searching with a different name or keyword.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      case "PendingPosts":
        return (
          <div className="">
            <div className=" flex items-center justify-between border border-gray-200 rounded-lg px-6 py-4 mb-2">
              <div className="flex flex-col ">
                <p>Review new Posts</p>{" "}
                <p>
                  Select which posts require admin review. Pending posts expire
                  in 14 days from the posting date.
                </p>
              </div>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-full">
                Set up
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg">
              <div className="flex flex-col items-center justify-center  border-b border-gray-200 px-6 py-4">
                <img src="/Images/group/81.jpg" className="size-40" />
                <p className="text-2xl font-semibold my-2">
                  Post's review ins't set up yet
                </p>
                <p className="w-72 text-center text-gray-500">
                  Group admins can turn on the ability to review group posts
                  before they’re shared with members.
                </p>
              </div>
            </div>
          </div>
        );
      default:
        return <p>Select a menu item to view details.</p>;
    }
  };

  return (
    <>
      {isloading ? (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <img src="/Images/loader.gif" className="h-16 w-16 bg-opacity-50" />
        </div>
      ) : (
        <div className="bg-white border-t border-gray-200">
          <div className="border-b border-gray-200 mt-16">
            <Link to={`/groups/${slug}`}>
              <div className="container mx-auto flex flex-col items-center lg:flex-row py-2 px-4 sm:px-6 lg:px-8">
                <img
                  className="w-10 h-10  mr-3"
                  src={groupdata.image}
                  alt={groupdata.group_name}
                />
                <p className="text-xl font-semibold ">{groupdata.group_name}</p>
              </div>
            </Link>
          </div>
          <div className="border-t border-gray-200">
            <div className="container mx-auto flex lg:flex-row py-4 px-4 sm:px-6 lg:px-8 text-2xl">
              Manage group
            </div>
            <Box sx={{ width: "100%", typography: "body1" }}>
              <TabContext value={tabValue || 1}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList
                    onChange={handleTabChange}
                    aria-label="Dynamic Tabs Example"
                    textColor="primary"
                    indicatorColor="primary"
                    className="tabs"
                  >
                    {tabs.map((tab) => (
                      <Tab
                        key={tab.value}
                        label={tab.label}
                        value={tab.value}
                        sx={{
                          color: tabValue === tab.value ? "#1976D2" : "#757575",
                          fontWeight:
                            tabValue === tab.value ? "bold" : "normal",
                          fontSize: "16px",
                          mx: 4,
                          textTransform: "capitalize",
                          "&:hover": {
                            backgroundColor: "#F3F4F6",
                          },
                        }}
                      />
                    ))}
                  </TabList>
                </Box>

                <TabPanel value="1">
                  <div className="container mx-auto flex px-4 sm:px-6 lg:px-8">
                    <div className="w-60 h-full bg-white rounded-lg shadow-md border border-gray-200">
                      {menuItems.map((item) => (
                        <div
                          key={item}
                          className={`p-2 cursor-pointer ${
                            menuItem === item
                              ? "border-l-4 border-green-700 font-bold text-green-700 bg-gray-100 pl-4"
                              : "text-gray-700 pl-4"
                          }`}
                          onClick={() => handleMenuChange(item)}
                        >
                          {item === "Requests" ? (
                            <>
                              Requests{" "}
                              {requestcount > 0 && (
                                <span className="">({requestcount})</span>
                              )}
                            </>
                          ) : (
                            item
                          )}
                        </div>
                      ))}
                    </div>
                    <div className=" ml-4 w-full ">
                      {renderMembershipContent()}
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value="2">
                  <div className="container mx-auto flex px-4 sm:px-6 lg:px-8">
                    <div className="w-60 h-full bg-white rounded-lg shadow-md border border-gray-200">
                      {contentItems.map((item) => (
                        <div
                          key={item}
                          className={`p-2 cursor-pointer ${
                            menuItem === item
                              ? "border-l-4 border-green-700 font-bold text-green-700 bg-gray-100 pl-4"
                              : "text-gray-700 pl-4"
                          }`}
                          onClick={() => handleContentChange(item)}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                    <div className=" ml-4 w-full ">
                      {renderMembershipContent()}
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value="3"></TabPanel>
              </TabContext>
            </Box>
          </div>
        </div>
      )}
    </>
  );
};

export default GroupManage;